'use client';

import classNames from 'classnames';
import { ComponentProps, type JSX, ReactNode, useId, useState } from 'react';
import Typography from '@alltrails/core/components/Typography';
import VerticalSlide from '@alltrails/core/components/VerticalSlide';
import Chevron from '../../icons/Chevron';
import setDenaliAttribute from '../../utils/setDenaliAttribute';
import styles from './styles/styles.module.scss';

export type AccordionProps = {
  ariaLevel?: number;
  borderBottomContainerClassName?: string;
  buttonContainerClassName?: string;
  children: ReactNode;
  contentClassName?: string;
  eyebrow?: ReactNode;
  externalControls?: {
    isOpen: boolean;
    toggleIsOpen: () => void;
  };
  headingTypography?: ComponentProps<typeof Typography>['variant'];
  initiallyOpen?: boolean;
  leadingVisual?: JSX.Element;
  subtitle?: ReactNode;
  subtitleTypography?: ComponentProps<typeof Typography>['variant'];
  title: ReactNode;
  trailingText?: string;
  variant?: 'default' | 'minimal';
};

const headingVariantClass = {
  heading100: 'heading100',
  heading200: 'heading200',
  heading300: 'heading300',
  heading400: 'heading400',
  heading500: 'heading500',
  heading600: 'heading600',
  heading700: 'heading700'
} as const;

type HeadingVariant = keyof typeof headingVariantClass;

export default function Accordion({
  borderBottomContainerClassName,
  children,
  buttonContainerClassName,
  contentClassName,
  eyebrow,
  externalControls,
  headingTypography,
  initiallyOpen = false,
  leadingVisual,
  subtitle,
  subtitleTypography = 'text100',
  title,
  trailingText,
  variant = 'default'
}: AccordionProps) {
  const [internalIsOpen, setInternalIsOpen] = useState(initiallyOpen);

  const toggleOnClick = () => {
    if (externalControls) {
      externalControls.toggleIsOpen();
    } else {
      setInternalIsOpen(prevState => !prevState);
    }
  };

  const isOpen = externalControls ? externalControls.isOpen : internalIsOpen;
  const titleHeadingClass =
    headingTypography && headingTypography in headingVariantClass ? styles[headingVariantClass[headingTypography as HeadingVariant]] : undefined;

  const verticalSlideId = useId();

  return (
    <>
      <div {...setDenaliAttribute('Accordion')} className={classNames(styles.container, buttonContainerClassName)}>
        <button className={styles.trigger} type="button" onClick={toggleOnClick} aria-expanded={isOpen} aria-controls={verticalSlideId}>
          <div className={styles.headerContent}>
            {leadingVisual && <div className={styles.leadingVisual}>{leadingVisual}</div>}
            <div className={styles.headerText}>
              <div className={styles.heading}>
                {eyebrow && (
                  <Typography className={styles.eyebrow} variant="text100" color="secondary">
                    {eyebrow}
                  </Typography>
                )}
                {titleHeadingClass ? (
                  <span className={classNames(styles.title, titleHeadingClass)}>{title}</span>
                ) : (
                  <Typography className={styles.title} variant={headingTypography ?? 'text200'}>
                    {title}
                  </Typography>
                )}
                {subtitle && (
                  <Typography className={styles.subtitle} variant={subtitleTypography} color="secondary">
                    {subtitle}
                  </Typography>
                )}
              </div>
            </div>
          </div>
          <div>
            {variant === 'minimal' ? (
              <div className={styles.row}>
                {trailingText && <div className={styles.trailingText}>{trailingText}</div>}
                <Chevron size="sm" color="--color-text-secondary" orientation={isOpen ? 'up' : 'down'} />
              </div>
            ) : (
              <Chevron color="--color-text-secondary" orientation={isOpen ? 'up' : 'down'} />
            )}
          </div>
        </button>
      </div>
      <VerticalSlide id={verticalSlideId} className={contentClassName} ignoreInitialOpeningAnimation={initiallyOpen} isOpen={isOpen}>
        {children}
      </VerticalSlide>
      <div className={classNames(styles.borderBottomContainer, borderBottomContainerClassName)}>
        <div className={styles.borderBottom} />
      </div>
    </>
  );
}
